<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { checkQrCodeEditablePermission } from '@ilteducation/customer-mgmt-permissions';

import { useLocale } from '../../composables/useLocale';
import useModal, { modals } from '../..//composables/useModal';
import { usePermissions } from '../../composables/usePermissions';
import { useSelectedItems } from '../../composables/useSelectedItems';
import { useCreateLoginCards } from '../LoginCard/useCreateLoginCards';
import { useCurrentOrganization } from '../../composables/useCurrentOrganization';

import SidePanel from '../SidePanel/SidePanel.vue';
import SidePanelSection from '../SidePanel/SidePanelSection.vue';
import SidePanelSectionButtonItem from '../SidePanel/SidePanelSectionButtonItem.vue';
import DeleteGroupsModal from './DeleteGroupsModal.vue';
import CreateMultipleLoginCardsModal from '../LoginCard/CreateMultipleLoginCardsModal.vue';

const { t } = useLocale();
const { selectedItems, clearSelectedItems } = useSelectedItems();
const { open: openDeleteGroupsModal } = useModal(modals.DELETE_GROUPS);
const { organization } = useCurrentOrganization();

const { createLoginCardsForMultipleGroupUsers } = useCreateLoginCards();
const { permissionContext, getCallerAssociation, reporterBoolean } = usePermissions();

defineProps<{
  totalGroups?: number;
}>();
const emit = defineEmits(['close', 'groups-deleted']);

const groupsDeleted = (groupUids: string[]) => {
  emit('groups-deleted', groupUids);
  emit('close');
};

const checkingQrCodeEditability = ref(false);
const isQrCodeEditableForSelectedGroups = ref(false);
watch(
  selectedItems,
  async () => {
    checkingQrCodeEditability.value = true;
    isQrCodeEditableForSelectedGroups.value = await checkQrCodeEditablePermission(
      permissionContext.value,
      { organization: organization.value, role: 'student' },
      getCallerAssociation,
      reporterBoolean,
    );

    checkingQrCodeEditability.value = false;
  },
  { immediate: true },
);

const onClose = () => {
  clearSelectedItems();
  emit('close');
};

const title = computed(() => t(`lcp.groups_selected_count`, { count: selectedItems.value.length }));
</script>

<template>
  <SidePanel :title="title" selected-entity="group" no-icon no-edit :on-close="onClose">
    <SidePanelSection :title="t('lcp.group_login')" :loading="checkingQrCodeEditability">
      <SidePanelSectionButtonItem
        v-if="selectedItems && isQrCodeEditableForSelectedGroups"
        :text="t('lcp.generate_login_cards')"
        icon="mdi-qrcode"
        :tooltip-text="t('lcp.create_qr_code_for_users')"
        :loading="checkingQrCodeEditability"
        @click="() => createLoginCardsForMultipleGroupUsers(selectedItems, { fileName: 'login-cards-groups' })"
      />
    </SidePanelSection>
    <slot name="settings" :selectedGroups="selectedItems"></slot>
    <template #append>
      <SidePanelSection>
        <v-row class="justify-end" no-gutters>
          <v-col cols="auto">
            <v-btn color="error" prepend-icon="mdi-trash-can" @click="() => openDeleteGroupsModal()">
              {{ t('lcp.delete_groups') }}
            </v-btn>
          </v-col>
        </v-row>
      </SidePanelSection>
    </template>
  </SidePanel>
  <DeleteGroupsModal :name="modals.DELETE_GROUPS" :group-uids="selectedItems" @groups-deleted="groupsDeleted" />
  <CreateMultipleLoginCardsModal createQrCodeSelected />
</template>

<style scoped lang="scss"></style>
